import { Close, Search } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { DataGrid, GridCellParams } from "@mui/x-data-grid";
import get from "lodash/get";
import { useCallback, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import BranchGroupEditModal from "../../components/BranchGroupEditModal";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useAlert from "../../hooks/useAlert";
import useNotification from "../../hooks/useNotification";
import service from "../../services/group";
import config from "../../utils/config";
import queryDefault from "../../utils/queryDefault";

const CustomNoRowOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      Sorry, data is not found
    </Typography>
  </Box>
);

export default function BranchGroup() {
  const [pageSize, setPageSize] = useState<number>(10);
  const navigate = useNavigate();
  const notification = useNotification();
  const alert = useAlert();
  const [uploading, setUploading] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedEditId, setSelectedEditId] = useState<number | null>(null);
  const [options, setOptions] = useState<any>({
    keyword: "",
  });
  const handleOnCellClick = (params: GridCellParams) => {
    const { row } = params;
    navigate(`/app/branch-group/${row.id}`, { state: row });
  };
  const listBranchGroup = useQuery(
    ["branchGroup", options],
    ({ queryKey }) => {
      return service.getAllBranchGroup({
        ...queryKey[1],
      });
    },
    {
      ...queryDefault,
    }
  );

  const deleteBranch = useMutation("delete-branch", service.deleteBranchGroup, {
    onSuccess: () => {
      listBranchGroup.refetch();
      notification.onOpen({
        message: "Group was successfully deleted!",
        type: "success",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const downloadTemplate = useMutation(
    "download-branch-group-template",
    service.downloadBranchGroupTemplate
  );

  const uploadBulk = useMutation(
    "upload-branch-group-template",
    service.uploadBranchGroupTemplate,
    {
      onSuccess: () => {
        setUploading(false);
        listBranchGroup.refetch();
        notification.onOpen({
          message: "File was successfully uploaded!",
          type: "success",
          position: "top",
        });
      },
      onError: () => {
        setUploading(false);
        notification.onOpen({
          message: "Something went wrong when uploading branch group template",
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );
  const form = useForm({
    defaultValues: {
      keyword: "",
    },
  });
  const onDelete = (id: number) => {
    alert.onClose();
    deleteBranch.mutate(id);
  };
  const onClickDelete = (id: number) => {
    alert.onOpen({
      title: "Confirm Delete?",
      message: "Are you sure want to delete group?",
      actions: [
        {
          color: "error",
          variant: "contained",
          children: "Delete",
          onClick: () => onDelete(id),
        },
        {
          color: "primary",
          variant: "outlined",
          children: "Cancel",
          onClick: () => alert.onClose(),
        },
      ],
    });
  };

  const onHandleSearch = useCallback((values: { keyword: any }) => {
    setOptions((prev: any) => ({
      ...prev,
      keyword: values.keyword,
    }));
  }, []);

  const handleUploadFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const extension = file.name.split(".").pop()?.toLowerCase();
    if (extension !== "xls" && extension !== "xlsx") {
      notification.onOpen({
        message: "Please upload excel file only (.xls or .xlsx)",
        type: "error",
        position: "top",
      });
      event.target.value = "";
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);
    uploadBulk.mutate(formData);
    event.target.value = "";
  };

  const onOpenEditModal = (id: number) => {
    setSelectedEditId(id);
    setEditModalOpen(true);
  };

  const onCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedEditId(null);
  };
  return (
    <MainLayout>
      <Helmet>
        <title>BRANCH GROUP</title>
      </Helmet>

      <Breadcrumb
        label="Branch Group"
        breadcrumbs={[{ label: "Group", href: "/app/branch-group" }]}
      />

      <ProtectedPermissionPage acceptPermissions={[1, 2]}>
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={1}>
            <Controller
              name="keyword"
              control={form.control}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder="Input Keyword here"
                  {...field}
                  {...config.onEnter(() => {
                    form.handleSubmit(onHandleSearch)();
                  })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        onClick={() => {
                          if (field.value) {
                            field.onChange("");
                            setOptions((prev: any) => ({
                              ...prev,
                              keyword: "",
                            }));
                          }
                        }}
                      >
                        {field.value ? (
                          <Close color="error" sx={{ cursor: "pointer" }} />
                        ) : (
                          <Search />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Button
              variant="outlined"
              onClick={form.handleSubmit(onHandleSearch)}
            >
              SEARCH
            </Button>
          </Stack>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              onClick={() => downloadTemplate.mutate()}
              disabled={downloadTemplate.isLoading}
            >
              Download Template
            </Button>
            <LoadingButton variant="contained" component="label" loading={uploading}>
              Upload Bulk
              <input onChange={handleUploadFile} type="file" hidden accept=".xls,.xlsx" />
            </LoadingButton>
            <Button
              variant="contained"
              component={RouterLink}
              to="/app/branch-group/create"
            >
              Add Group
            </Button>
          </Stack>
        </Box>

        <Box sx={{ mt: 2 }}>
          <DataGrid
            autoHeight
            loading={listBranchGroup.isLoading}
            getRowId={(record) => get(record, "id")}
            rows={get(listBranchGroup, "data.data", [])}
            rowCount={get(listBranchGroup, "data.data", []).length}
            page={options.page_number}
            pageSize={pageSize}
            rowsPerPageOptions={[10, 20, 30]}
            pagination
            disableSelectionOnClick
            onPageChange={(newPage) => {
              setOptions((prev: any) => ({ ...prev, page_number: newPage }));
            }}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            components={{
              NoRowsOverlay: CustomNoRowOverlay,
            }}
            columns={[
              {
                field: "name",
                headerName: "Name",
                flex: 1,
                filterable: false,
                minWidth: 200,
                renderCell: (params) => {
                  return (
                    <Stack onClick={() => handleOnCellClick(params)}>
                      <Typography color="blue">{params.value}</Typography>
                    </Stack>
                  );
                },
              },
              {
                field: "region",
                headerName: "Region",
                flex: 1,
                filterable: false,
                minWidth: 150,
                renderCell: (params) => {
                  return (
                    <Stack>
                      <Typography>{params.value}</Typography>
                    </Stack>
                  );
                },
              },
              {
                field: "category",
                headerName: "Category",
                flex: 1,
                filterable: false,
                minWidth: 150,
                renderCell: (params) => {
                  return (
                    <Stack>
                      <Typography textTransform="capitalize">{params.value || "-"}</Typography>
                    </Stack>
                  );
                },
              },
              {
                field: "kode_cabang",
                headerName: "Kode Cabang",
                flex: 1,
                filterable: false,
                minWidth: 150,
                renderCell: (params) => {
                  return (
                    <Stack>
                      <Typography>{params.value}</Typography>
                    </Stack>
                  );
                },
              },
              {
                field: "actions",
                headerName: "Action",
                sortable: false,
                filterable: false,
                width: 200,
                renderCell: (params) => {
                  return (
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => onOpenEditModal(Number(get(params, "row.id")))}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => onClickDelete(params.row.id)}
                      >
                        Delete
                      </Button>
                    </Stack>
                  );
                },
              },
            ]}
            sx={{
              ".MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
              },
            }}
          />
        </Box>

        <BranchGroupEditModal
          open={editModalOpen}
          id={selectedEditId}
          onClose={onCloseEditModal}
          onSaved={() => {
            listBranchGroup.refetch();
          }}
        />
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
